import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Loader2, CheckCircle2 } from "lucide-react";
import { defaultScheduleValue } from "../../lib/apptDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConfirmDialog = ({ target, token, onClose, onConfirmed }) => {
  const [scheduledAt, setScheduledAt] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [baseFee, setBaseFee] = useState(125);
  const [farmCall, setFarmCall] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (target) {
      setScheduledAt(target.scheduled_at || defaultScheduleValue(target));
      setCustomNote(target.custom_note || "");
      setBaseFee(target.amount && target.amount > 0 ? target.amount : 125);
      setFarmCall(false);
    }
  }, [target]);

  const amount = Number(baseFee || 0) + (farmCall ? 50 : 0);

  const submit = async () => {
    if (!scheduledAt) {
      toast.error("Please pick a date and time.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/appointments/${target.id}/confirm`,
        { scheduled_at: scheduledAt, custom_note: customNote, amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onConfirmed(data);
      toast.success(
        data.confirmation_sent
          ? "Confirmed — prep email sent to the owner."
          : "Confirmed. (Email could not be sent — check Resend.)"
      );
      onClose();
    } catch {
      toast.error("Could not confirm. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="confirm-dialog" className="bg-sand border-charcoal/10">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-charcoal font-light">
            Confirm appointment
          </DialogTitle>
          <DialogDescription className="text-stone">
            Set the date &amp; time for {target?.name}
            {target?.horse_name ? ` and ${target.horse_name}` : ""}. We'll email the owner asking them to
            have the horse stalled and groomed by then.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <label className="text-xs uppercase tracking-[0.2em] text-clay">Scheduled date &amp; time</label>
          <input
            data-testid="confirm-datetime"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="mt-3 w-full bg-transparent border border-charcoal/20 rounded-xl px-4 py-3 text-charcoal focus:outline-none focus:border-clay transition-colors"
          />

          <label className="block mt-5 text-xs uppercase tracking-[0.2em] text-clay">Personal note to the owner <span className="text-stone/60 normal-case tracking-normal">(optional)</span></label>
          <textarea
            data-testid="confirm-note"
            rows={3}
            value={customNote}
            onChange={(e) => setCustomNote(e.target.value)}
            placeholder="e.g. Please leave a halter on, and the barn is the second gate on the left."
            className="mt-3 w-full bg-transparent border border-charcoal/20 rounded-xl px-4 py-3 text-charcoal placeholder:text-stone/50 focus:outline-none focus:border-clay transition-colors resize-none"
          />
          <p className="mt-2 text-xs text-stone/70">This note will be added to the confirmation email the owner receives.</p>

          <div className="grid grid-cols-2 gap-3 items-center mt-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-clay">Session fee ($)</label>
              <input
                data-testid="confirm-fee"
                type="number"
                min="0"
                step="5"
                value={baseFee}
                onChange={(e) => setBaseFee(e.target.value)}
                className="mt-2 w-full bg-transparent border border-charcoal/20 rounded-xl px-4 py-2.5 text-charcoal focus:outline-none focus:border-clay transition-colors"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-charcoal mt-6 cursor-pointer">
              <input data-testid="confirm-farmcall" type="checkbox" checked={farmCall} onChange={(e) => setFarmCall(e.target.checked)} className="accent-clay w-4 h-4" />
              Travel over 1 hr (+$50)
            </label>
          </div>
          <p className="mt-3 text-sm text-stone">Total fee: <span className="font-heading text-lg text-charcoal">${amount}</span></p>
        </div>

        <div className="flex justify-end gap-3 mt-2">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-full border border-charcoal/15 text-stone hover:text-charcoal hover:border-charcoal text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="confirm-submit"
            onClick={submit}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-clay text-sand hover:bg-clay-dark text-sm font-medium transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} strokeWidth={1.5} />}
            {loading ? "Sending..." : "Confirm & send email"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
